// An app icon upload from the HQ console (spec/14 §6, ADR-0028): the same three steps as a trailer
// (`trailer-upload.ts`) — reserve, PUT the bytes straight to object storage with the signed URL, complete —
// so the image never passes through the api. On completion the server checks the bytes really are a PNG or
// WebP and copies them to a content-addressed key; the answer is the app with its new `iconUrl`.
//
// The types and the size cap come from the contract's `CreateAppIconUpload` (vite/contract-schemas.ts), so the
// console refuses exactly what the server would, before a byte is sent.

import type { ApiClient, Game, GameId, IconContentType } from '@clavian/api-client';
import { $defs } from 'virtual:clavian/contract-schemas';

import { type PutWithProgress, xhrPut } from './trailer-upload';

export type IconUploadFailure = 'wrongType' | 'tooLarge' | 'failed';

export class IconUploadError extends Error {
  override readonly name = 'IconUploadError';
  readonly reason: IconUploadFailure;
  constructor(reason: IconUploadFailure) {
    super(reason);
    this.reason = reason;
  }
}

const iconProperties = (($defs['CreateAppIconUpload']?.['properties'] ?? {}) as Record<string, Record<string, unknown>>);

export const ICON_TYPES: readonly string[] = (($defs['IconContentType']?.['enum'] ?? []) as string[]).filter((type) => type !== '');
export const ICON_MAX_BYTES: number = (iconProperties['bytes']?.['maximum'] ?? Number.POSITIVE_INFINITY) as number;

/** Why a file cannot be an icon, before anything is sent — or `undefined` when it can. */
export function iconFileProblem(file: File): IconUploadFailure | undefined {
  if (!ICON_TYPES.includes(file.type)) return 'wrongType';
  if (file.size === 0) return 'failed';
  if (file.size > ICON_MAX_BYTES) return 'tooLarge';
  return undefined;
}

/** Reserve, upload and complete one app icon. Throws `IconUploadError` for a refusal before or during the PUT. */
export async function uploadAppIcon(
  api: ApiClient,
  id: GameId,
  file: File,
  onProgress: (fraction: number) => void = () => undefined,
  put: PutWithProgress = xhrPut,
): Promise<Game> {
  const problem = iconFileProblem(file);
  if (problem !== undefined) throw new IconUploadError(problem);

  const reserved = await api.createAppIconUpload({ path: { id }, body: { contentType: file.type as IconContentType, bytes: file.size } });
  // Exactly the Content-Type the signature covers, and the body whose length it covers (spec/08 §4.19).
  const sent = await put(reserved.upload.url, reserved.upload.contentType, file, onProgress).catch(() => false);
  if (!sent) throw new IconUploadError('failed');
  onProgress(1);
  return api.completeAppIconUpload({ path: { id, uploadId: reserved.uploadId } });
}

/** A byte count as the copy shows a size cap: whole kilobytes under a megabyte, else megabytes. */
export function formatIconCap(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / (1024 * 1024))} MB` : `${Math.round(bytes / 1024)} KB`;
}
